import React from "react";
import { Navigate } from "react-router-dom";

// Every page is its own download, so a client opening a gallery link never
// loads the studio workspace. Signed-in studios preload the workspace pages.
const pages = {};
const RELOAD_KEY = "studiozflo:page-reload";
// A tab opened before a deploy asks for page files that no longer exist; reload once to get the new build.
const loadPage = (load) => () => load().then((module) => {
  try { window.sessionStorage.removeItem(RELOAD_KEY); } catch {}
  return module;
}, (error) => {
  let reloaded = true;
  try { reloaded = window.sessionStorage.getItem(RELOAD_KEY) === "1"; window.sessionStorage.setItem(RELOAD_KEY, "1"); } catch {}
  if (error?.name !== "ChunkLoadError" || reloaded) throw error;
  window.location.reload();
  return new Promise(() => {});
});
const page = (name, load, preload = false) => {
  pages[name] = { load, preload };
  return React.lazy(loadPage(load));
};

const Login = page("Login", () => import("../pages/Auth/Login"));
const SignUp = page("SignUp", () => import("../pages/Auth/SignUp"));
const Dashboard = page("Dashboard", () => import("../pages/Dashboard/Dashboard"), true);
const NewTransfer = page("NewTransfer", () => import("../pages/Transfers/NewTransfer"), true);
const TransferDetails = page("TransferDetails", () => import("../pages/Transfers/TransferDetails"), true);
const TransferHistory = page("TransferHistory", () => import("../pages/Transfers/TransferHistory"), true);
const Settings = page("Settings", () => import("../pages/Settings/Settings"), true);
const Landing = page("Landing", () => import("../pages/Landing/Landing"));
const PrivacyPolicy = page("PrivacyPolicy", () => import("../pages/Legal/PrivacyPolicy"));
const TermsOfService = page("TermsOfService", () => import("../pages/Legal/TermsOfService"));
const ContactSupport = page("ContactSupport", () => import("../pages/Support/ContactSupport"));
const NotFound = page("NotFound", () => import("../pages/NotFound/NotFound"));
const ClientList = page("ClientList", () => import("../pages/Clients/ClientList"), true);
const ClientDetails = page("ClientDetails", () => import("../pages/Clients/ClientDetails"), true);
const Events = page("Events", () => import("../pages/Events/Events"), true);
const Team = page("Team", () => import("../pages/Team/Team"), true);
const Owner = page("Owner", () => import("../pages/Owner/Owner"), true);
const AccessPending = page("AccessPending", () => import("../pages/AccessPending/AccessPending"), true);
const ClientPortal = page("ClientPortal", () => import("../pages/ClientPortal/ClientPortal"));
const SelectedPhotos = page("SelectedPhotos", () => import("../pages/ClientPortal/SelectedPhotos"));
const EditedGallery = page("EditedGallery", () => import("../pages/Galleries/EditedGallery"));
const ComingSoon = page("ComingSoon", () => import("../pages/ComingSoon/ComingSoon"), true);
const HowItWorks = page("HowItWorks", () => import("../pages/HowItWorks/HowItWorks"));
const Finance = page("Finance", () => import("../pages/Finance/Finance"), true);

// Fetch the workspace pages in the background, so moving between them never waits.
export const preloadWorkspacePages = () => Object.values(pages).forEach(({ load, preload }) => preload && load().catch(() => {}));

export const routes = [
  { path: "/finance", type: "private", pageKey: "finance", element: <Finance /> },
  { path: "/security", element: <Navigate to="/#security" replace /> },
  { path: "/how-it-works", type: "public", element: <HowItWorks /> },
  { path: "/coming-soon", type: "private", element: <ComingSoon /> },
  { path: "/gallery/:token", element: <EditedGallery /> },
  { path: "/galleries/new", type: "protected", pageKey: "galleries", element: <Navigate to="/transfers/new?tab=edit" replace /> },
  { path: "/select/:token/selected", element: <SelectedPhotos /> },
  { path: "/select/:token", element: <ClientPortal /> },
  { path: "/events", type: "private", pageKey: "events", element: <Events /> },
  { path: "/team", type: "private", pageKey: "team", element: <Team /> },
  { path: "/owner", type: "private", ownerOnly: true, element: <Owner /> },
  { path: "/access-pending", type: "private", element: <AccessPending /> },
  {
    path: "/onboarding",
    type: "private",
    // Preserve old bookmarks without presenting a retired setup flow.
    element: <Navigate to="/dashboard" replace />,
  },
  {
    path: "/clients/:id/transfers/new",
    type: "protected", pageKey: "galleryCreation",
    element: <NewTransfer />,
  },
  {
    path: "/clients",
    type: "private", pageKey: "clients",
    element: <ClientList />,
  },
  {
    path: "/clients/new",
    type: "private", pageKey: "clients",
    element: <ClientList />,
  },
  {
    path: "/clients/:id",
    type: "private", pageKey: "clients",
    element: <ClientDetails />,
  },
  {
    path: "/",
    element: <Landing />,
  },
  {
    path: "/login",
    type: "public",
    element: <Login />,
  },
  {
    path: "/signup",
    type: "public",
    element: <SignUp />,
  },
  {
    path: "/privacy",
    element: <PrivacyPolicy />,
  },
  {
    path: "/terms",
    element: <TermsOfService />,
  },
  {
    path: "/contact",
    type: "public",
    element: <ContactSupport />,
  },
  {
    path: "/support",
    type: "public",
    element: <ContactSupport />,
  },
  {
    path: "/dashboard",
    type: "private", pageKey: "dashboard",
    element: <Dashboard />,
  },
  {
    path: "/transfers/new",
    type: "protected", pageKey: "galleryCreation",
    element: <NewTransfer />,
  },
  {
    path: "/transfers/history",
    type: "protected", pageKey: "selections",
    element: <TransferHistory />,
  },
  {
    path: "/transfers/:id",
    type: "protected", pageKey: "selections",
    element: <TransferDetails />,
  },
  {
    path: "/settings",
    type: "private", pageKey: "settings",
    element: <Settings />,
  },
  {
    path: "/not-found",
    element: <NotFound />,
  },
  {
    path: "*",
    element: <Navigate to="/not-found" replace />,
  },
];
